import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Users, Plus, LogIn, Search, Compass, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { EmptyState, SkeletonList } from "@/components/Mascot";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { PaidNote } from "@/components/Paywall";

const CARD = "rounded-2xl border border-white/10 bg-[#121725] p-5";
const members = (n) => `${n} ${n === 1 ? "member" : "members"}`;

function GroupCard({ c }) {
  return (
    <Link to={`/app/groups/${c.class_id}`} data-testid={`class-card-${c.class_id}`}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:bg-white/[0.06] hover:-translate-y-0.5 transition-[transform,background-color]">
      <p className="font-display font-medium break-words">{c.name}</p>
      <p className="text-xs text-neutral-500 mt-1">{members(c.member_count)}{c.listed ? " · Listed in Discover" : ""}</p>
      <p className="text-xs font-mono text-sky-300 mt-3">Code {c.join_code}</p>
    </Link>
  );
}

function CreateJoin({ paid, onDone }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [params] = useSearchParams();
  const nameRef = useRef(null);
  const codeRef = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    const a = params.get("action");
    (a === "create" ? nameRef : a === "join" ? codeRef : null)?.current?.focus();
  }, [params, paid]);
  const act = async (fn, msg) => {
    try { const { data } = await fn(); toast.success(msg); onDone(); navigate(`/app/groups/${data.class_id}`); } catch (e) { toast.error(formatError(e)); }
  };
  const create = () => name.trim() && act(() => http.post("/groups", { name }), "Study group created");
  const join = () => code.trim() && act(() => http.post("/groups/join", { code }), "Joined study group");
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {paid ? (
        <div className={CARD}>
          <p className="text-sm font-medium mb-3 flex items-center gap-2"><Plus className="h-4 w-4 text-sky-300" /> Start a study group</p>
          <div className="flex gap-2">
            <Input ref={nameRef} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="e.g. Algebra II study crew" className="bg-white/5 border-white/10" data-testid="class-name-input" aria-label="Group name" />
            <button onClick={create} aria-label="Create group" className="px-4 rounded-lg bg-sky-400 text-white text-sm font-medium hover:bg-sky-500" data-testid="class-create-btn"><Plus className="h-4 w-4" /></button>
          </div>
        </div>
      ) : <PaidNote text="Starting a study group is on paid plans. You can join any group with a code or from Discover." testid="class-create-locked" />}
      <div className={CARD}>
        <p className="text-sm font-medium mb-3 flex items-center gap-2"><LogIn className="h-4 w-4 text-emerald-300" /> Join with a code</p>
        <div className="flex gap-2">
          <Input ref={codeRef} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && join()} placeholder="6-letter code" maxLength={6} className="bg-white/5 border-white/10 font-mono tracking-widest" data-testid="class-code-input" aria-label="Group code" />
          <button onClick={join} className="px-4 rounded-lg border border-white/15 text-sm hover:bg-white/5" data-testid="class-join-btn">Join</button>
        </div>
      </div>
    </div>
  );
}

function DiscoverCard({ g, onJoined }) {
  const [busy, setBusy] = useState(false);
  const join = async () => {
    setBusy(true);
    try { const { data } = await http.post(`/groups/${g.class_id}/join-listed`); toast.success(`Joined ${g.name}`); onJoined(data); }
    catch (e) { toast.error(formatError(e)); setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex flex-col" data-testid={`discover-card-${g.class_id}`}>
      <p className="font-display font-medium break-words">{g.name}</p>
      <p className="text-xs text-neutral-500 mt-1">{members(g.member_count)}</p>
      {g.description && <p className="text-sm text-neutral-400 mt-3 break-words">{g.description}</p>}
      <button onClick={join} disabled={busy} data-testid={`discover-join-${g.class_id}`}
        className="mt-4 self-start text-sm px-4 py-1.5 rounded-full border border-emerald-300/30 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50 flex items-center gap-1.5">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogIn className="h-3.5 w-3.5" />} Join group
      </button>
    </div>
  );
}

function Discover() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const t = setTimeout(() => http.get("/groups/discover", { params: { q } }).then((r) => setRows(r.data)).catch((e) => { toast.error(formatError(e)); setRows([]); }), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <section data-testid="discover-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-medium flex items-center gap-2"><Compass className="h-4 w-4 text-violet-300" /> Discover groups</h2>
          <p className="text-sm text-neutral-500">Groups whose members chose to list them. Anyone can join and play.</p>
        </div>
        <div className="relative sm:w-64">
          <Search className="h-4 w-4 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by subject or name" className="pl-9 bg-white/5 border-white/10" data-testid="discover-search-input" aria-label="Search listed groups" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5" data-testid="discover-list">
        {!rows && <SkeletonList rows={2} />}
        {rows && !rows.length && <p className="text-sm text-neutral-500" data-testid="discover-empty">{q ? "No listed groups match that search." : "No groups are listed yet."}</p>}
        {rows?.map((g) => <DiscoverCard key={g.class_id} g={g} onJoined={(d) => navigate(`/app/groups/${d.class_id}`)} />)}
      </div>
    </section>
  );
}

export default function ClassesPage() {
  const [classes, setClasses] = useState(null);
  const { ent } = useAuth();
  const paid = !!ent?.features.groups;
  const load = useCallback(() => http.get("/groups").then((r) => setClasses(r.data)).catch((e) => { toast.error(formatError(e)); setClasses([]); }), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="flex-1 overflow-y-auto scroll-thin">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-10">
        <header>
          <p className="inline-flex items-center gap-2 text-xs text-violet-300 border border-violet-300/30 bg-violet-500/10 rounded-full px-3 py-1 mb-4"><Users className="h-3 w-3" /> Study together</p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Groups</h1>
          <p className="text-neutral-400 mt-3 max-w-xl">Study with classmates: share notes, play quiz games and ask the tutor about what the group shares.</p>
        </header>
        <CreateJoin paid={paid} onDone={load} />
        <section data-testid="my-groups-section">
          <h2 className="font-display text-lg font-medium">My groups</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4" data-testid="class-list">
            {!classes && <SkeletonList rows={2} />}
            {classes && !classes.length && <EmptyState testid="classes-empty" pose="thinking" title="No groups yet" text="Join one with a code, or find one below." />}
            {classes?.map((c) => <GroupCard key={c.class_id} c={c} />)}
          </div>
        </section>
        <Discover />
      </div>
    </div>
  );
}
